import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { siteInfo } from '@/mocks/articles';

export default function AboutPage() {
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formResponseMsg, setFormResponseMsg] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = (formData.get('contact_alt') as string || '').trim();

    if (honeypotValue) {
      setFormStatus('success');
      setFormResponseMsg('消息已发送！');
      setFormName('');
      setFormEmail('');
      setFormMessage('');
      return;
    }

    if (formMessage.length > 500) {
      setFormStatus('error');
      setFormResponseMsg('消息内容不能超过500个字符。');
      return;
    }

    setFormStatus('loading');

    try {
      const body = new URLSearchParams({
        name: formName,
        email: formEmail,
        message: formMessage,
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d9qq916e3oq4jqumton0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body,
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};

      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (response.ok && parsed.code === 'OK') {
        setFormStatus('success');
        setFormResponseMsg('感谢您的留言！我们会尽快回复您。');
        setFormName('');
        setFormEmail('');
        setFormMessage('');
      } else {
        setFormStatus('error');
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormResponseMsg(serverMsg.includes('spam') ? '发送失败，请稍后重试。' : serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormResponseMsg('网络错误，请检查网络连接后重试。');
    }
  };

  const faqItems = [
    {
      q: '狗头萝莉视频网站提供哪些内容？',
      a: '我们的网站专注于收集和整理狗头萝莉的精彩视频内容，涵盖直播录屏、搞笑片段、日常Vlog、精彩集锦、粉丝互动以及最新动态等多个分类。无论你是新粉丝还是老粉丝，都能在这里找到你喜欢的内容。',
    },
    {
      q: '网站的视频多久更新一次？',
      a: '我们致力于每日更新网站内容，确保粉丝能够在第一时间看到狗头萝莉的最新视频。团队会及时跟进直播动态，将精彩片段整理并发布到对应分类中。',
    },
    {
      q: '如何订阅网站更新通知？',
      a: '你可以在首页底部的订阅区域输入邮箱地址，订阅我们的更新通知。我们会在有新视频发布时第一时间通过邮件通知你。你也可以关注我们的社交媒体账号获取最新动态。',
    },
    {
      q: '网站的视频可以下载吗？',
      a: '目前网站仅提供在线观看服务，暂不支持视频下载。我们建议你通过书签收藏喜欢的视频页面，方便随时回来观看。',
    },
    {
      q: '如何联系我们？',
      a: '你可以通过页面下方的联系表单给我们留言，或者直接发送邮件至 contact@goutouluoli.com。我们会在收到消息后的24小时内回复你。',
    },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        name: `关于我们 - ${siteInfo.name}`,
        description: `${siteInfo.name}品牌介绍、口碑评价及联系方式`,
        url: `${siteInfo.url}/about`,
        isPartOf: {
          '@type': 'WebSite',
          name: siteInfo.name,
          url: siteInfo.url,
        },
      },
      {
        '@type': 'Organization',
        name: siteInfo.name,
        url: siteInfo.url,
        description: siteInfo.description,
        email: siteInfo.email,
        foundingDate: siteInfo.foundingYear,
        address: {
          '@type': 'PostalAddress',
          addressLocality: '上海',
          addressCountry: 'CN',
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqItems.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.a,
          },
        })),
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Navbar />

      <main className="pt-20">
        <section className="bg-background-100 border-b border-background-200/70">
          <div className="px-4 md:px-6 py-12 md:py-16">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950">
                关于我们
              </h1>
              <p className="mt-5 text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                狗头萝莉视频是一个专注于收集和分享狗头萝莉精彩视频内容的粉丝平台。
                我们致力于为每一位狗头萝莉的粉丝提供最全面、最及时的视频内容合集。
              </p>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                <div className="text-center p-6 rounded-lg bg-background-100">
                  <span className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center mx-auto rounded-full bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-eye-line text-2xl"></i>
                  </span>
                  <h3 className="text-4xl font-heading font-bold text-foreground-950">500+</h3>
                  <p className="mt-2 text-sm text-foreground-500">精彩视频内容</p>
                </div>
                <div className="text-center p-6 rounded-lg bg-background-100">
                  <span className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center mx-auto rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className="ri-user-heart-line text-2xl"></i>
                  </span>
                  <h3 className="text-4xl font-heading font-bold text-foreground-950">200万+</h3>
                  <p className="mt-2 text-sm text-foreground-500">忠实粉丝群体</p>
                </div>
                <div className="text-center p-6 rounded-lg bg-background-100">
                  <span className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center mx-auto rounded-full bg-secondary-100 text-secondary-600 mb-4">
                    <i className="ri-calendar-check-line text-2xl"></i>
                  </span>
                  <h3 className="text-4xl font-heading font-bold text-foreground-950">{siteInfo.foundingYear}</h3>
                  <p className="mt-2 text-sm text-foreground-500">创立年份</p>
                </div>
              </div>

              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
                品牌故事
              </h2>
              <div className="space-y-5 text-foreground-700 leading-relaxed text-sm md:text-base">
                <p>
                  狗头萝莉视频网成立于{siteInfo.foundingYear}年，由一群热爱狗头萝莉的忠实粉丝共同创建。
                  我们深知狗头萝莉的粉丝遍布全国各地，但直播内容转瞬即逝，很多精彩瞬间如果不能及时记录就会永远错过。
                  因此，我们决定建立一个专门的视频内容聚合平台，让每一位粉丝都能方便地回顾和欣赏狗头萝莉的精彩时刻。
                </p>
                <p>
                  从最初的简单收集到如今的系统化分类整理，我们不断优化网站体验，扩充内容库。
                  目前网站已收录超过500个精选视频，涵盖直播录屏、搞笑片段、日常Vlog、精彩集锦、粉丝互动和最新动态六大分类，
                  累计服务超过200万粉丝用户。
                </p>
                <p>
                  我们坚持每日更新，确保粉丝能在第一时间看到最新内容。每一段视频都经过精心筛选和剪辑，
                  只为呈现最精彩的狗头萝莉给每一位来访的粉丝。未来我们将继续努力，不断丰富内容类型，提升用户体验，
                  打造最好的狗头萝莉视频内容平台。
                </p>
              </div>

              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-14 mb-6">
                口碑评价
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {[
                  {
                    name: '小明',
                    text: '终于找到一个可以系统看狗头萝莉视频的网站了！分类很清晰，更新也很及时，每天必刷的网站。',
                    rating: 5,
                  },
                  {
                    name: '叮当猫',
                    text: '作为狗头萝莉的三年老粉，这个网站真的帮了大忙。以前总是错过直播，现在可以在这里补看录屏，太方便了！',
                    rating: 5,
                  },
                  {
                    name: '追风少年',
                    text: '视频质量很高，编辑很用心。特别是精彩集锦分类，每个视频都让人看得停不下来。强烈推荐给所有粉丝！',
                    rating: 5,
                  },
                ].map((review, idx) => (
                  <div key={idx} className="p-5 md:p-6 bg-background-100 rounded-lg">
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(review.rating)].map((_, i) => (
                        <span key={i} className="w-4 h-4 flex items-center justify-center text-accent-500">
                          <i className="ri-star-fill text-sm"></i>
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      &ldquo;{review.text}&rdquo;
                    </p>
                    <p className="text-xs text-foreground-500 font-medium">— {review.name}</p>
                  </div>
                ))}
              </div>

              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-14 mb-6">
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </span>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-950">电子邮箱</h4>
                      <p className="text-sm text-foreground-500 mt-1">{siteInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </span>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-950">公司地址</h4>
                      <p className="text-sm text-foreground-500 mt-1">{siteInfo.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </span>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-950">工作时间</h4>
                      <p className="text-sm text-foreground-500 mt-1">周一至周五 9:00 - 18:00</p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleContactSubmit} data-readdy-form="contact-form" className="bg-background-100 rounded-lg p-5 md:p-6">
                  <div className="honeypot-field">
                    <input
                      type="text"
                      name="contact_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        value={formEmail}
                        onChange={(e) => setFormEmail(e.target.value)}
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
                        placeholder="请输入您的邮箱地址"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        留言内容
                        <span className="text-foreground-400 ml-1">({formMessage.length}/500)</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        value={formMessage}
                        onChange={(e) => setFormMessage(e.target.value)}
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 resize-none"
                        placeholder="请输入您的留言内容（不超过500字符）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm rounded-lg transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                    >
                      {formStatus === 'loading' ? '发送中...' : '发送留言'}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center font-medium">{formResponseMsg}</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center font-medium">{formResponseMsg}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-10 text-center">
                常见问题
              </h2>
              <div className="space-y-4">
                {faqItems.map((item, idx) => (
                  <details key={idx} className="group bg-background-50 rounded-lg p-5 md:p-6 cursor-pointer">
                    <summary className="flex items-center justify-between text-base font-heading font-semibold text-foreground-950 list-none">
                      {item.q}
                      <span className="w-6 h-6 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform duration-200">
                        <i className="ri-arrow-down-s-line text-lg"></i>
                      </span>
                    </summary>
                    <p className="mt-4 text-sm text-foreground-600 leading-relaxed border-t border-background-200/70 pt-4">
                      {item.a}
                    </p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}